import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Box, Tooltip } from '@components/common';
import ScheduleBoard from '@components/ScheduleBoard';
import ScheduleActions, { ScheduleSelectors } from '@redux/ScheduleRedux';
import { AppSelectors } from '@redux/AppRedux';
import DragHandle from '@components/draggable/DraggableIcon';
import SvgIcon from '@components/SvgIcon';
import { DRAG_ITEM_TYPES } from '@redux/DragRedux';
import useDetectSize from '@hooks/useDetectSize';
import { colors } from '@styles/theme';

const SpecialItemIcon = styled(SvgIcon)``;

const DRAG_POSITIONS_LEFT = {
  mobile: [40, 20, 40, 20],
  pad: [44, 2, 44, 2],
  desktop: [58, 12, 58, 12]
};

const DRAG_POSITIONS_TOP = {
  mobile: [40, 40, 20, 20],
  pad: [44, 44, 2, 2],
  desktop: [58, 58, 10, 10]
};

function AddonBoardView(props) {
  const {
    selectedItems,
    numOfRows,
    removeItem,
    moveItem,
    serviceTypeData
  } = props;

  const isMobile = useDetectSize();
  const specialItems = selectedItems.reduce((acc, item) => {
    const { row, col, serviceType, addons } = item;
    if (!acc[`row_${row}`]) {
      acc[`row_${row}`] = {};
    }
    acc[`row_${row}`][`col_${col}`] = {
      row,
      col,
      serviceType,
      addons
    };
    return acc;
  }, {});

  const filledExtractor = () => {
    return false;
  };
  const disabledExtractor = (row, col, specialItem) => !specialItem;

  const handleOnDrop = (item, row, col, addon) => {
    if (row === -1 || col === -1) {
      removeItem({
        row: item.row,
        col: item.col,
        addon
      });
      return;
    }

    moveItem(
      {
        row: item.row,
        col: item.col,
        addon
      },
      {
        row,
        col,
        addon
      }
    );
  };

  const renderItem = (specialItem, disabled) => {
    const { serviceType, addons } = specialItem;

    const serviceTypeDetail = serviceTypeData[serviceType];
    const { fullTitle } = serviceTypeDetail;
    const iconFill = disabled ? 'darkGray' : undefined;

    const iconProps = isMobile
      ? { width: 24, height: 24 }
      : { width: 16, height: 16 };

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems={['center', 'flex-start']}
        justifyContent={['center', 'flex-start']}
        width={[52, 72, 80]}
        position="relative"
        flex={1}
      >
        <Tooltip
          animation="scale"
          delay={[300, 0]}
          duration={300}
          content={fullTitle}
          theme="light-border"
          placement="bottom"
        >
          <span>
            <SpecialItemIcon
              name={serviceType}
              fill={iconFill ? colors[iconFill] : undefined}
              disabled={disabled}
              {...iconProps}
            />
          </span>
        </Tooltip>
        {addons &&
          addons.map((addon, index) => {
            return (
              <DragHandle
                style={{
                  position: 'absolute',
                  margin: 0
                }}
                left={[
                  DRAG_POSITIONS_LEFT['mobile'][index],
                  DRAG_POSITIONS_LEFT['pad'][index],
                  DRAG_POSITIONS_LEFT['desktop'][index]
                ]}
                top={[
                  DRAG_POSITIONS_TOP['mobile'][index],
                  DRAG_POSITIONS_TOP['pad'][index],
                  DRAG_POSITIONS_TOP['desktop'][index]
                ]}
                key={addon}
                icon={addon}
                color="gold"
                disabled={disabled}
                dragType={DRAG_ITEM_TYPES.REMOVABLE}
                data={addon}
                onDrop={(row, col) => {
                  handleOnDrop(specialItem, row, col, addon);
                }}
              />
            );
          })}
      </Box>
    );
  };
  return (
    <Box display="flex" flexDirection="row">
      <ScheduleBoard
        numOfRows={numOfRows}
        specialItems={specialItems}
        renderSpecialItem={renderItem}
        disabledExtractor={disabledExtractor}
        filledExtractor={filledExtractor}
      />
    </Box>
  );
}

AddonBoardView.propTypes = {
  selectedItems: PropTypes.array,
  numOfRows: PropTypes.number,
  moveItem: PropTypes.func,
  removeItem: PropTypes.func,
  serviceTypeData: PropTypes.object
};

const mapStatesToProps = state => ({
  selectedItems: ScheduleSelectors.selectScheduledItems(state),
  numOfRows: ScheduleSelectors.selectNumOfRows(state),
  serviceTypeData: AppSelectors.selectServiceTypeData(state)
});

const mapDispatchToProps = dispatch => ({
  removeItem: item => dispatch(ScheduleActions.detachAddonToItem(item)),
  moveItem: (item, newItem) =>
    dispatch(ScheduleActions.moveAddonToItem(item, newItem))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(AddonBoardView);
